import { Rights } from '@dokucoach/common-types';
import { useContextAuthManager } from 'components/AuthManager';
import ComingSoon from 'components/ComingSoon';
import Layout from 'components/Layout';
import PermissionError from 'components/PermissionError';
import ScrollToTop from 'components/ScrollToTop';
import { default as React, useMemo } from 'react';
import { Route, Switch } from 'react-router-dom';
import { getRoute } from 'utils/path';
import PrivateRoute from './components/AuthManager/PrivateRoute';
import PublicRoute from './components/AuthManager/PublicRoute';
import routePaths from './constants/route-paths';
import { ListRoutes } from './routes/Lists';

// NOTE(emmchris, 2025/11):
// Language support usually gets you the wrong type of import:
// ```ts
// import Asd from pages/Asd
// ```
//
// Instead, here we prefer lazy-loading pages, so this should be manually fixed
// every once in a while.
//
// Also, entries are sorted alphabetically.
const BiographyForm = React.lazy(() => import('pages/BiographyForm'));
const BookingAccounts = React.lazy(() => import('pages/Configuration/BookingAccounts'));
const CarePlanning = React.lazy(() => import('pages/CarePlanning'));
const CashBalance = React.lazy(() => import('pages/Invoicing/CashBalance'));
const CashItems = React.lazy(() => import('pages/Configuration/CashItems'));
const ConsultationForm = React.lazy(() => import('pages/ConsultationForm'));
const Dashboard = React.lazy(() => import('pages/Dashboard'));
const DecubitusRiskForm = React.lazy(() => import('pages/DecubitusRiskForm'));
const DoctorGeneralForm = React.lazy(() => import('pages/BaseData/DoctorGeneralForm'));
const DoctorsDiagnosisForm = React.lazy(() => import('pages/DoctorsDiagnosisForm'));
const EmployeeContractForm = React.lazy(() => import('pages/BaseData/EmployeeContractForm'));
const EmployeeGeneralForm = React.lazy(() => import('pages/BaseData/EmployeeGeneralForm'));
const EmployeeSystemAccessForm = React.lazy(
  () => import('pages/BaseData/EmployeeSystemAccessForm'),
);
const FallProtocol = React.lazy(() => import('pages/FallProtocol'));
const FibuCashList = React.lazy(() => import('pages/Invoicing/Fibu/FibuCash'));
const FibuConfiguration = React.lazy(() => import('pages/Configuration/Fibu'));
const FibuList = React.lazy(() => import('pages/Invoicing/Fibu'));
const HospitalReport = React.lazy(() => import('pages/HospitalReport'));
const HouseDivision = React.lazy(() => import('pages/Configuration/HouseDivision'));
const IndicatorsArchive = React.lazy(() => import('pages/Indicators/Archive'));
const IndicatorsAudit = React.lazy(() => import('pages/Indicators/Audit'));
const IndicatorsConfiguration = React.lazy(() => import('pages/Configuration/Indicators'));
const IndicatorsTransmission = React.lazy(() => import('pages/Indicators/Transmission'));
const Institution = React.lazy(() => import('pages/Configuration/Institution'));
const InsuranceGeneralForm = React.lazy(() => import('pages/BaseData/InsuranceGeneralForm'));
const Invoice = React.lazy(() => import('pages/Invoicing/Invoice'));
const InvoiceOverview = React.lazy(() => import('pages/Invoicing/InvoiceOverview'));
const InvoicingBalances = React.lazy(() => import('pages/Invoicing/InvoicingBalances'));
const InvoicingCare = React.lazy(() => import('pages/Invoicing/InvoicingCare'));
const InvoicingConfiguration = React.lazy(() => import('pages/Configuration/Invoicing'));
const InvoicingExecutionPlanPreview = React.lazy(
  () => import('pages/Invoicing/ExecutionPlanPreview'),
);
const InvoicingIncomes = React.lazy(() => import('pages/Invoicing/InvoicingIncomes'));
const InvoicingOpenPayments = React.lazy(() => import('pages/Invoicing/InvoicingOpenPayments'));
const InvoicingOverview = React.lazy(() => import('pages/Invoicing/InvoicingOverview'));
const Login = React.lazy(() => import('pages/Login'));
const MedicationSheet = React.lazy(() => import('pages/MedicationSheet'));
const MonthlyOverview = React.lazy(() => import('pages/MonthlyOverview'));
const MovementStatus = React.lazy(() => import('pages/MovementStatus'));
const NurseVisit = React.lazy(() => import('pages/NurseVisit'));
const PflegegradAssessment = React.lazy(() => import('pages/PflegegradAssessment'));
const NursingReport = React.lazy(() => import('pages/NursingForm'));
const NutritionProtocolPage = React.lazy(() => import('pages/NutritionProtocol'));
const PainProtocol = React.lazy(() => import('pages/PainProtocol'));
const ResidentAdviceConsultationForm = React.lazy(
  () => import('pages/BaseData/ResidentAdviceConsultationForm'),
);
const ResidentChecklistForm = React.lazy(() => import('pages/BaseData/ResidentChecklistForm'));
const ResidentContactsForm = React.lazy(() => import('pages/BaseData/ResidentContactsForm'));
const ResidentContractForm = React.lazy(() => import('pages/BaseData/ResidentContractForm'));
const ResidentDocumentManagement = React.lazy(
  () => import('pages/BaseData/ResidentDocumentManagement'),
);
const ResidentGeneralForm = React.lazy(() => import('pages/BaseData/ResidentGeneralForm'));
const ResidentInvoiceForm = React.lazy(() => import('pages/BaseData/ResidentInvoiceForm'));
const ResidentPastoralCareForm = React.lazy(
  () => import('pages/BaseData/ResidentPastoralCareForm'),
);
const ResidentUtensilsForm = React.lazy(() => import('pages/BaseData/ResidentUtensilsForm'));
const ResidentsWithoutInvoice = React.lazy(() => import('pages/Invoicing/ResidentsWithoutInvoice'));
const RiskOfFallingForm = React.lazy(() => import('pages/RiskOfFallingForm'));
const RosterConfiguration = React.lazy(() => import('pages/Configuration/Roster'));
const SepaList = React.lazy(() => import('pages/Invoicing/Sepa'));
const SepaPocketList = React.lazy(() => import('pages/Invoicing/SepaPocket'));
const SocialCare = React.lazy(() => import('pages/SocialCare'));
const SuggestionsConfiguration = React.lazy(() => import('pages/Configuration/Suggestions'));
const Verify = React.lazy(() => import('pages/Verify'));
const VisitSheet = React.lazy(() => import('pages/VisitSheet'));
const VitalInformationForm = React.lazy(() => import('pages/VitalInformationForm'));
const VitalSign = React.lazy(() => import('pages/VitalSign'));
const WelfareOfficeGeneralForm = React.lazy(
  () => import('pages/BaseData/WelfareOfficeGeneralForm'),
);
const WoundDocumentation = React.lazy(() => import('pages/WoundDocumentation'));

const Router = () => {
  const { user, hasPermission, hasOnePermission } = useContextAuthManager();

  const isInvoicingEnabled = useMemo(() => {
    return user?.nursingHome.license.invoicingEnabledFrom !== null;
  }, [user]);

  return (
    <React.Fragment>
      <ScrollToTop />
      <Switch>
        <Route exact path={routePaths.VERIFY} component={Verify} />
        <PublicRoute exact path={routePaths.LOGIN} component={Login} />
        <Layout>
          <PrivateRoute exact path={routePaths.HOME} component={() => null} />
          <PrivateRoute exact path={routePaths.DASHBOARD} component={() => null} />
          <PrivateRoute exact path={routePaths.BASE_DATA} component={() => null} />
          <PrivateRoute exact path={routePaths.INDICATORS} component={() => null} />
          <PrivateRoute exact path={routePaths.CONFIGURATION} component={() => null} />
          <PrivateRoute exact path={routePaths.INVOICING} component={() => null} />

          {/* LISTS */}
          <PrivateRoute exact path={routePaths.LISTS} component={() => null} />
          <ListRoutes />

          {/* GROUP */}
          <PrivateRoute
            exact
            path={routePaths.GROUP}
            component={() =>
              hasOnePermission([Rights.CARE.TASKS.READ, Rights.CARE.EXECUTION.READ]) ? (
                <Dashboard />
              ) : (
                <PermissionError />
              )
            }
          />
          {/* RESIDENT*/}
          <PrivateRoute
            exact
            path={getRoute([routePaths.GROUP, routePaths.RESIDENT])}
            component={() =>
              hasOnePermission([Rights.CARE.TASKS.READ, Rights.CARE.EXECUTION.READ]) ? (
                <Dashboard />
              ) : (
                <PermissionError />
              )
            }
          />
          {/* HEALTH */}
          <PrivateRoute
            exact
            path={getRoute([routePaths.GROUP, routePaths.RESIDENT, routePaths.HEALTH])}
            component={() => null}
          />
          <PrivateRoute
            exact
            path={getRoute([routePaths.GROUP, routePaths.RESIDENT, routePaths.HEALTH_MEDICATION])}
            component={() =>
              hasPermission(Rights.CARE.MEDICATION.READ) ? <MedicationSheet /> : <PermissionError />
            }
          />
          <PrivateRoute
            exact
            path={getRoute([routePaths.GROUP, routePaths.RESIDENT, routePaths.HEALTH_VISIT_SHEET])}
            component={() =>
              hasPermission(Rights.CARE.VISIT_SHEET.READ) ? <VisitSheet /> : <PermissionError />
            }
          />
          <PrivateRoute
            exact
            path={getRoute([
              routePaths.GROUP,
              routePaths.RESIDENT,
              routePaths.HEALTH_DOCTORS_DIAGNOSES,
            ])}
            component={() =>
              /* TODO: Assign correct right */
              hasPermission(Rights.CARE.DOCTORS_DIAGNOSIS.READ) ? (
                <DoctorsDiagnosisForm />
              ) : (
                <PermissionError />
              )
            }
          />
          <PrivateRoute
            exact
            path={getRoute([
              routePaths.GROUP,
              routePaths.RESIDENT,
              routePaths.HEALTH_VITAL_INFORMATION,
            ])}
            component={() =>
              /* TODO: Assign correct right */
              hasPermission(Rights.CARE.VITAL_INFORMATIONS.READ) ? (
                <VitalInformationForm />
              ) : (
                <PermissionError />
              )
            }
          />
          {/* ASSESSMENTS */}
          <PrivateRoute
            exact
            path={getRoute([routePaths.GROUP, routePaths.RESIDENT, routePaths.ASSESSMENTS])}
            component={() => null}
          />
          <PrivateRoute
            exact
            path={getRoute([routePaths.GROUP, routePaths.RESIDENT, routePaths.MOVEMENT_STATUS])}
            component={() =>
              hasPermission(Rights.CARE.MOVEMENT_STATUS.READ) ? (
                <MovementStatus />
              ) : (
                <PermissionError />
              )
            }
          />
          <PrivateRoute
            exact
            path={getRoute([routePaths.GROUP, routePaths.RESIDENT, routePaths.DECUBITUS_RISK])}
            component={() =>
              hasPermission(Rights.CARE.DECUBITUS_RISK.READ) ? (
                <DecubitusRiskForm />
              ) : (
                <PermissionError />
              )
            }
          />
          <PrivateRoute
            exact
            path={getRoute([routePaths.GROUP, routePaths.RESIDENT, routePaths.RISK_OF_FALLING])}
            component={() =>
              hasPermission(Rights.CARE.FALL_RISK.READ) ? (
                <RiskOfFallingForm />
              ) : (
                <PermissionError />
              )
            }
          />
          {/* CARE PLAN */}
          <PrivateRoute
            exact
            path={getRoute([routePaths.GROUP, routePaths.RESIDENT, routePaths.CARE_PLAN])}
            component={() =>
              hasPermission(Rights.CARE.CARE_PLAN.READ) ? <CarePlanning /> : <PermissionError />
            }
          />
          {/* EXECUTION */}
          <PrivateRoute
            exact
            path={getRoute([routePaths.GROUP, routePaths.RESIDENT, routePaths.EXECUTION])}
            component={() => null}
          />
          <PrivateRoute
            exact
            path={getRoute([routePaths.GROUP, routePaths.RESIDENT, routePaths.EX_MONTHLY_OVERVIEW])}
            component={() =>
              hasPermission(Rights.CARE.TASKS.READ) ? <MonthlyOverview /> : <PermissionError />
            }
          />
          <PrivateRoute
            exact
            path={getRoute([routePaths.GROUP, routePaths.RESIDENT, routePaths.EX_SOCIAL_CARE])}
            component={() =>
              hasPermission(Rights.CARE.SOCIAL_CARE.READ) ? <SocialCare /> : <PermissionError />
            }
          />
          <PrivateRoute
            exact
            path={getRoute([routePaths.GROUP, routePaths.RESIDENT, routePaths.EX_MAINTENNANCE])}
            /* TODO: Assign correct right */
            component={() => <ComingSoon title="Durchführungsnachweis" />}
          />
          {/* NURSING REPORT */}
          <PrivateRoute
            exact
            path={getRoute([routePaths.GROUP, routePaths.RESIDENT, routePaths.NURSING])}
            component={() =>
              hasPermission(Rights.CARE.NURSING_REPORT.READ) ? (
                <NursingReport />
              ) : (
                <PermissionError />
              )
            }
          />
          {/* LOGS */}
          <PrivateRoute
            exact
            path={getRoute([routePaths.GROUP, routePaths.RESIDENT, routePaths.LOGS])}
            component={() => null}
          />
          <PrivateRoute
            exact
            path={getRoute([routePaths.GROUP, routePaths.RESIDENT, routePaths.WOUND_DOCUMENTATION])}
            component={() =>
              hasPermission(Rights.CARE.WOUND_DOCUMENTATION.READ) ? (
                <WoundDocumentation />
              ) : (
                <PermissionError />
              )
            }
          />
          <PrivateRoute
            exact
            path={getRoute([routePaths.GROUP, routePaths.RESIDENT, routePaths.PAIN_ASSESSMENT])}
            component={() =>
              hasPermission(Rights.GLOBAL_ADMIN.READ) ||
              hasPermission(Rights.CARE.PAIN_PROTOCOL.READ) ? (
                <PainProtocol />
              ) : (
                <PermissionError />
              )
            }
          />
          <PrivateRoute
            exact
            path={getRoute([
              routePaths.GROUP,
              routePaths.RESIDENT,
              routePaths.NUTRITION_DRINKING_LOG,
            ])}
            component={() =>
              hasPermission(Rights.CARE.NUTRITION_PROTOCOL.READ) ? (
                <NutritionProtocolPage />
              ) : (
                <PermissionError />
              )
            }
          />
          <PrivateRoute
            exact
            path={getRoute([routePaths.GROUP, routePaths.RESIDENT, routePaths.FALL_PROTOCOL])}
            component={() =>
              hasPermission(Rights.CARE.FALL_PROTOCOL.READ) ? <FallProtocol /> : <PermissionError />
            }
          />
          <PrivateRoute
            exact
            path={getRoute([routePaths.GROUP, routePaths.RESIDENT, routePaths.VITAL_SIGN])}
            component={() =>
              hasPermission(Rights.CARE.VITAL_SIGNS.READ) ? <VitalSign /> : <PermissionError />
            }
          />
          {/* CONSULTATION */}
          <PrivateRoute
            exact
            path={getRoute([routePaths.GROUP, routePaths.RESIDENT, routePaths.CONSULTATION])}
            component={() =>
              hasPermission(Rights.CARE.CONSULTATION_FORM.READ) ? (
                <ConsultationForm />
              ) : (
                <PermissionError />
              )
            }
          />
          {/* BIOGRAPHY */}
          <PrivateRoute
            exact
            path={getRoute([routePaths.GROUP, routePaths.RESIDENT, routePaths.BIOGRAPHY])}
            component={() =>
              hasPermission(Rights.CARE.BIOGRAPHY.READ) ? <BiographyForm /> : <PermissionError />
            }
          />
          {/* RESIDENT*/}
          <PrivateRoute
            exact
            path={getRoute([
              routePaths.GROUP,
              routePaths.RESIDENT,
              `${routePaths.BIOGRAPHY}/read-only`,
            ])}
            component={() =>
              hasPermission(Rights.CARE.BIOGRAPHY.READ) ? <BiographyForm /> : <PermissionError />
            }
          />
          {/* NURSE VISIT */}
          <PrivateRoute
            exact
            path={getRoute([routePaths.GROUP, routePaths.RESIDENT, routePaths.NURSE_VISIT])}
            component={() =>
              hasPermission(Rights.CARE.NURSE_VISIT.READ) ? <NurseVisit /> : <PermissionError />
            }
          />
          {/* HOSPITAL REPORT */}
          <PrivateRoute
            exact
            path={getRoute([routePaths.GROUP, routePaths.RESIDENT, routePaths.REPORT])}
            component={() =>
              hasPermission(Rights.CARE.REPORT.READ) ? <HospitalReport /> : <PermissionError />
            }
          />
          {/* PFLEGEGRAD ASSESSMENT */}
          <PrivateRoute
            exact
            path={getRoute([
              routePaths.GROUP,
              routePaths.RESIDENT,
              routePaths.PFLEGEGRAD_ASSESSMENT,
            ])}
            component={() =>
              hasPermission(Rights.CARE.PFLEGEGRAD_ASSESSMENT.READ) ? (
                <PflegegradAssessment />
              ) : (
                <PermissionError />
              )
            }
          />
          {/* EXAL */}
          <PrivateRoute
            exact
            path={getRoute([routePaths.GROUP, routePaths.RESIDENT, routePaths.EXAL_CARE_ROUNDS])}
            component={() =>
              /* TODO: Assign correct right */
              hasPermission(Rights.CARE.CARE_ROUNDS.READ) ? (
                <ComingSoon title="Pflegevisite" />
              ) : (
                <PermissionError />
              )
            }
          />
          <PrivateRoute
            exact
            path={getRoute([routePaths.GROUP, routePaths.RESIDENT, routePaths.EXAL_APPRAISAL])}
            /* TODO: Assign correct right */
            component={() => <ComingSoon title="Begutachtung" />}
          />
          <PrivateRoute
            exact
            path={getRoute([routePaths.GROUP, routePaths.RESIDENT, routePaths.EXAL_INDICATORS])}
            component={() =>
              hasPermission(Rights.CARE.INDICATORS.READ) ? (
                <ComingSoon title="Indikatoren" />
              ) : (
                <PermissionError />
              )
            }
          />
          <PrivateRoute
            exact
            path={getRoute([
              routePaths.GROUP,
              routePaths.RESIDENT,
              routePaths.EXAL_CARE_DISCUSSION,
            ])}
            component={() =>
              hasPermission(Rights.CARE.CASE_DISCUSSION.READ) ? (
                <ComingSoon title="Fallbesprechung" />
              ) : (
                <PermissionError />
              )
            }
          />
          <PrivateRoute
            exact={false}
            path={getRoute([routePaths.DATA_EMPLOYEE, routePaths.DATA_GENERAL])}
            component={() =>
              hasPermission(Rights.EMPLOYEES.GENERAL.READ) ? (
                <EmployeeGeneralForm />
              ) : (
                <PermissionError baseData />
              )
            }
          />
          <PrivateRoute
            exact={false}
            path={getRoute([routePaths.DATA_EMPLOYEE, routePaths.DATA_CONTRACT])}
            component={() =>
              hasPermission(Rights.EMPLOYEES.CONTRACT.READ) ? (
                <EmployeeContractForm />
              ) : (
                <PermissionError baseData />
              )
            }
          />
          <PrivateRoute
            exact={false}
            path={getRoute([routePaths.DATA_EMPLOYEE, routePaths.DATA_ACCESS])}
            component={() =>
              hasPermission(Rights.EMPLOYEES.SYSTEM_ACCESS.READ) ? (
                <EmployeeSystemAccessForm />
              ) : (
                <PermissionError baseData />
              )
            }
          />
          <PrivateRoute
            exact
            path={routePaths.DATA_ADD_EMPLOYEE}
            component={() =>
              hasPermission(Rights.EMPLOYEES.WRITE) ? (
                <EmployeeGeneralForm />
              ) : (
                <PermissionError baseData />
              )
            }
          />
          <PrivateRoute
            exact={false}
            path={getRoute([routePaths.DATA_RESIDENT, routePaths.DATA_GENERAL])}
            component={() =>
              hasPermission(Rights.RESIDENTS.GENERAL.READ) ? (
                <ResidentGeneralForm />
              ) : (
                <PermissionError baseData />
              )
            }
          />
          <PrivateRoute
            exact={false}
            path={getRoute([routePaths.DATA_RESIDENT, routePaths.DATA_CONTRACT])}
            component={() =>
              hasPermission(Rights.RESIDENTS.CONTRACT.READ) ? (
                <ResidentContractForm />
              ) : (
                <PermissionError baseData />
              )
            }
          />
          <PrivateRoute
            exact={false}
            path={getRoute([routePaths.DATA_RESIDENT, routePaths.DATA_INVOICING])}
            component={() =>
              hasPermission(Rights.ADMINISTRATION_AND_INVOICING.INVOICE.READ) ? (
                <ResidentInvoiceForm />
              ) : (
                <PermissionError baseData />
              )
            }
          />
          <PrivateRoute
            exact={false}
            path={getRoute([routePaths.DATA_RESIDENT, routePaths.DATA_CHECKLIST])}
            component={() =>
              hasPermission(Rights.RESIDENTS.CHECKLIST.READ) ? (
                <ResidentChecklistForm />
              ) : (
                <PermissionError baseData />
              )
            }
          />
          <PrivateRoute
            exact={false}
            path={getRoute([routePaths.DATA_RESIDENT, routePaths.DATA_ADVICE_CONSULTATION])}
            component={() =>
              hasPermission(Rights.RESIDENTS.CONSULTATION.READ) ? (
                <ResidentAdviceConsultationForm />
              ) : (
                <PermissionError baseData />
              )
            }
          />
          <PrivateRoute
            exact={false}
            path={getRoute([routePaths.DATA_RESIDENT, routePaths.DATA_PASTORAL_CARE])}
            component={() =>
              hasPermission(Rights.RESIDENTS.PASTORAL_CARE.READ) ? (
                <ResidentPastoralCareForm />
              ) : (
                <PermissionError baseData />
              )
            }
          />
          <PrivateRoute
            exact={false}
            path={getRoute([routePaths.DATA_RESIDENT, routePaths.DATA_UTENSILS])}
            component={() =>
              hasPermission(Rights.RESIDENTS.UTILS.READ) ? (
                <ResidentUtensilsForm />
              ) : (
                <PermissionError baseData />
              )
            }
          />
          <PrivateRoute
            exact={false}
            path={getRoute([routePaths.DATA_RESIDENT, routePaths.DATA_CONTACTS])}
            component={() =>
              hasPermission(Rights.RESIDENTS.CONTACTS.READ) ? (
                <ResidentContactsForm />
              ) : (
                <PermissionError baseData />
              )
            }
          />
          <PrivateRoute
            exact={false}
            path={getRoute([routePaths.DATA_RESIDENT, routePaths.DATA_DOCUMENT_MANAGEMENT])}
            component={() =>
              hasPermission(Rights.RESIDENTS.DOCUMENT_MANAGEMENT.READ) ? (
                <ResidentDocumentManagement />
              ) : (
                <PermissionError baseData />
              )
            }
          />
          <PrivateRoute
            exact
            path={routePaths.DATA_ADD_RESIDENT}
            component={() =>
              hasPermission(Rights.RESIDENTS.WRITE) ? (
                <ResidentGeneralForm />
              ) : (
                <PermissionError baseData />
              )
            }
          />
          <PrivateRoute
            exact={false}
            path={getRoute([routePaths.DATA_DOCTOR, routePaths.DATA_GENERAL])}
            component={() =>
              hasPermission(Rights.BASE_DATA.DOCTORS.READ) ? (
                <DoctorGeneralForm />
              ) : (
                <PermissionError baseData />
              )
            }
          />
          <PrivateRoute
            exact
            path={routePaths.DATA_ADD_DOCTOR}
            component={() =>
              hasPermission(Rights.BASE_DATA.DOCTORS.WRITE) ? (
                <DoctorGeneralForm />
              ) : (
                <PermissionError baseData />
              )
            }
          />

          <PrivateRoute
            exact={false}
            path={getRoute([routePaths.DATA_INSURANCE, routePaths.DATA_GENERAL])}
            component={() =>
              hasPermission(Rights.BASE_DATA.INSURANCE.READ) ? (
                <InsuranceGeneralForm />
              ) : (
                <PermissionError baseData />
              )
            }
          />
          <PrivateRoute
            exact
            path={routePaths.DATA_ADD_INSURANCE}
            component={() =>
              hasPermission(Rights.BASE_DATA.INSURANCE.WRITE) ? (
                <InsuranceGeneralForm />
              ) : (
                <PermissionError baseData />
              )
            }
          />

          <PrivateRoute
            exact
            path={getRoute([routePaths.CONFIGURATION_HOUSE_DIVISIONS])}
            component={() =>
              hasPermission(Rights.ADMINISTRATION_AND_INVOICING.ADMINISTRATE.READ) ? (
                <HouseDivision />
              ) : (
                <PermissionError />
              )
            }
          />

          <PrivateRoute
            exact
            path={getRoute([routePaths.CONFIGURATION_FIBU])}
            component={() =>
              hasPermission(Rights.ADMINISTRATION_AND_INVOICING.ADMINISTRATE.READ) ? (
                <FibuConfiguration />
              ) : (
                <PermissionError />
              )
            }
          />

          <PrivateRoute
            exact
            path={getRoute([routePaths.CONFIGURATION_CARE_DOCUMENTATION])}
            component={() =>
              hasPermission(Rights.ADMINISTRATION_AND_INVOICING.ADMINISTRATE.READ) ? (
                <ComingSoon title="Pflegedokumentation" />
              ) : (
                <PermissionError />
              )
            }
          />

          <PrivateRoute
            exact
            path={getRoute([routePaths.CONFIGURATION_HOUSE_DATA])}
            component={() =>
              hasPermission(Rights.ADMINISTRATION_AND_INVOICING.ADMINISTRATE.READ) ? (
                <Institution />
              ) : (
                <PermissionError />
              )
            }
          />

          <PrivateRoute
            exact
            path={getRoute([routePaths.CONFIGURATION_ROSTER])}
            component={() =>
              hasPermission(Rights.ADMINISTRATION_AND_INVOICING.ADMINISTRATE.READ) ? (
                <RosterConfiguration />
              ) : (
                <PermissionError />
              )
            }
          />

          <PrivateRoute
            exact
            path={getRoute([routePaths.CONFIGURATION_INDICATORS_TRANSMISSION])}
            component={() =>
              hasPermission(Rights.ADMINISTRATION_AND_INVOICING.ADMINISTRATE.READ) ? (
                <IndicatorsConfiguration />
              ) : (
                <PermissionError />
              )
            }
          />
          <PrivateRoute
            exact
            path={getRoute([routePaths.CONFIGURATION_INVOICING])}
            component={() =>
              user?.nursingHome.license.invoicingEnabledFrom &&
              hasPermission(Rights.ADMINISTRATION_AND_INVOICING.ADMINISTRATE.READ) ? (
                <InvoicingConfiguration />
              ) : (
                <PermissionError />
              )
            }
          />

          <PrivateRoute
            exact
            path={getRoute([routePaths.CONFIGURATION_BOOKING_ACCOUNTS])}
            component={() =>
              user?.nursingHome.license.invoicingEnabledFrom &&
              hasPermission(Rights.ADMINISTRATION_AND_INVOICING.ADMINISTRATE.READ) ? (
                <BookingAccounts />
              ) : (
                <PermissionError />
              )
            }
          />

          <PrivateRoute
            exact
            path={getRoute([routePaths.CONFIGURATION_CASH_ITEMS])}
            component={() =>
              isInvoicingEnabled &&
              hasOnePermission([
                Rights.ADMINISTRATION_AND_INVOICING.INVOICE.READ,
                Rights.ADMINISTRATION_AND_INVOICING.INVOICE.WRITE,
              ]) ? (
                <CashItems />
              ) : (
                <PermissionError />
              )
            }
          />

          <PrivateRoute
            exact
            path={getRoute([routePaths.CONFIGURATION_SUGGESTIONS])}
            component={() =>
              hasPermission(Rights.ADMINISTRATION_AND_INVOICING.ADMINISTRATE.READ) ? (
                <SuggestionsConfiguration />
              ) : (
                <PermissionError />
              )
            }
          />

          <PrivateRoute
            exact
            path={getRoute([routePaths.INDICATORS_AUDIT])}
            component={() =>
              hasOnePermission([Rights.ADMINISTRATION_AND_INVOICING.ADMINISTRATE.READ]) ? (
                <IndicatorsAudit />
              ) : (
                <PermissionError />
              )
            }
          />

          <PrivateRoute
            exact
            path={getRoute([routePaths.INDICATORS_ARCHIVE])}
            component={() =>
              hasOnePermission([Rights.ADMINISTRATION_AND_INVOICING.ADMINISTRATE.READ]) ? (
                <IndicatorsArchive />
              ) : (
                <PermissionError />
              )
            }
          />

          <PrivateRoute
            exact
            path={getRoute([routePaths.INDICATORS_TRANSMISSION])}
            component={() =>
              hasOnePermission([
                Rights.ADMINISTRATION_AND_INVOICING.ADMINISTRATE.READ,
                Rights.ADMINISTRATION_AND_INVOICING.ADMINISTRATE.WRITE,
              ]) ? (
                <IndicatorsTransmission />
              ) : (
                <PermissionError />
              )
            }
          />

          <PrivateRoute
            exact
            path={getRoute([routePaths.INVOICING_OVERVIEW])}
            component={() =>
              isInvoicingEnabled &&
              hasOnePermission([
                Rights.ADMINISTRATION_AND_INVOICING.INVOICE.READ,
                Rights.ADMINISTRATION_AND_INVOICING.INVOICE.WRITE,
              ]) ? (
                //TODO: Delete the InvoiceOverview component with all of its dependencies
                // <InvoiceOverview mode={InvoiceViewMode.OVERVIEW} />
                <InvoicingOverview />
              ) : (
                <PermissionError />
              )
            }
          />

          <PrivateRoute
            exact
            path={getRoute([routePaths.INVOICING_OPEN])}
            component={() =>
              isInvoicingEnabled &&
              hasOnePermission([
                Rights.ADMINISTRATION_AND_INVOICING.INVOICE.READ,
                Rights.ADMINISTRATION_AND_INVOICING.INVOICE.WRITE,
              ]) ? (
                <InvoicingOpenPayments />
              ) : (
                <PermissionError />
              )
            }
          />

          <PrivateRoute
            exact
            path={getRoute([routePaths.INVOICING_BALANCES])}
            component={() =>
              isInvoicingEnabled &&
              hasOnePermission([
                Rights.ADMINISTRATION_AND_INVOICING.INVOICE.READ,
                Rights.ADMINISTRATION_AND_INVOICING.INVOICE.WRITE,
              ]) ? (
                <InvoicingBalances />
              ) : (
                <PermissionError />
              )
            }
          />

          <PrivateRoute
            exact
            path={getRoute([routePaths.INVOICING_INCOMES])}
            component={() =>
              isInvoicingEnabled &&
              hasOnePermission([
                Rights.ADMINISTRATION_AND_INVOICING.INVOICE.READ,
                Rights.ADMINISTRATION_AND_INVOICING.INVOICE.WRITE,
              ]) ? (
                <InvoicingIncomes />
              ) : (
                <PermissionError />
              )
            }
          />

          <PrivateRoute
            exact
            path={getRoute([routePaths.INVOICING_CARE])}
            component={() =>
              isInvoicingEnabled &&
              hasOnePermission([
                Rights.ADMINISTRATION_AND_INVOICING.INVOICE.READ,
                Rights.ADMINISTRATION_AND_INVOICING.INVOICE.WRITE,
              ]) ? (
                <InvoicingCare />
              ) : (
                <PermissionError />
              )
            }
          />

          <PrivateRoute
            exact
            path={getRoute([routePaths.RESIDENTS_WITHOUT_INVOICE])}
            component={() =>
              isInvoicingEnabled &&
              hasOnePermission([
                Rights.ADMINISTRATION_AND_INVOICING.INVOICE.READ,
                Rights.ADMINISTRATION_AND_INVOICING.INVOICE.WRITE,
              ]) ? (
                <ResidentsWithoutInvoice />
              ) : (
                <PermissionError />
              )
            }
          />

          <PrivateRoute
            exact
            path={getRoute([routePaths.SEPA])}
            component={() =>
              isInvoicingEnabled &&
              hasOnePermission([
                Rights.ADMINISTRATION_AND_INVOICING.INVOICE.READ,
                Rights.ADMINISTRATION_AND_INVOICING.INVOICE.WRITE,
              ]) ? (
                <SepaList />
              ) : (
                <PermissionError />
              )
            }
          />

          <PrivateRoute
            exact
            path={getRoute([routePaths.INVOICING_CASH_BALANCE])}
            component={() =>
              isInvoicingEnabled &&
              hasOnePermission([
                Rights.ADMINISTRATION_AND_INVOICING.INVOICE.READ,
                Rights.ADMINISTRATION_AND_INVOICING.INVOICE.WRITE,
              ]) ? (
                <CashBalance />
              ) : (
                <PermissionError />
              )
            }
          />

          <PrivateRoute
            exact
            path={getRoute([routePaths.FIBU])}
            component={() =>
              isInvoicingEnabled &&
              hasOnePermission([
                Rights.ADMINISTRATION_AND_INVOICING.INVOICE.READ,
                Rights.ADMINISTRATION_AND_INVOICING.INVOICE.WRITE,
              ]) ? (
                <FibuList />
              ) : (
                <PermissionError />
              )
            }
          />

          <PrivateRoute
            exact
            path={getRoute([routePaths.SEPA_POCKET])}
            component={() =>
              isInvoicingEnabled &&
              hasOnePermission([
                Rights.ADMINISTRATION_AND_INVOICING.INVOICE.READ,
                Rights.ADMINISTRATION_AND_INVOICING.INVOICE.WRITE,
              ]) ? (
                <SepaPocketList />
              ) : (
                <PermissionError />
              )
            }
          />

          <PrivateRoute
            exact
            path={getRoute([routePaths.FIBU_CASH])}
            component={() =>
              isInvoicingEnabled &&
              hasOnePermission([
                Rights.ADMINISTRATION_AND_INVOICING.INVOICE.READ,
                Rights.ADMINISTRATION_AND_INVOICING.INVOICE.WRITE,
              ]) ? (
                <FibuCashList />
              ) : (
                <PermissionError />
              )
            }
          />

          <PrivateRoute
            exact
            path={getRoute([routePaths.INVOICING, routePaths.INVOICE])}
            component={() =>
              isInvoicingEnabled &&
              hasOnePermission([
                Rights.ADMINISTRATION_AND_INVOICING.INVOICE.READ,
                Rights.ADMINISTRATION_AND_INVOICING.INVOICE.WRITE,
              ]) ? (
                <Invoice />
              ) : (
                <PermissionError />
              )
            }
          />

          <PrivateRoute
            exact
            path={getRoute([routePaths.INVOICING, routePaths.EXECUTION_PLAN_PREVIEW])}
            component={() =>
              isInvoicingEnabled &&
              hasOnePermission([Rights.ADMINISTRATION_AND_INVOICING.INVOICE.WRITE]) ? (
                <InvoicingExecutionPlanPreview />
              ) : (
                <PermissionError />
              )
            }
          />

          <PrivateRoute
            exact
            path={getRoute([routePaths.NOT_FOUND])}
            component={() => <ComingSoon title="" />}
          />
          <PrivateRoute
            exact={false}
            path={getRoute([routePaths.DATA_WELFARE_OFFICE, routePaths.DATA_GENERAL])}
            component={() =>
              hasPermission(Rights.BASE_DATA.WELFARE_OFFICE.READ) ? (
                <WelfareOfficeGeneralForm />
              ) : (
                <PermissionError baseData />
              )
            }
          />
          <PrivateRoute
            exact
            path={routePaths.DATA_ADD_WELFARE_OFFICE}
            component={() =>
              hasPermission(Rights.BASE_DATA.WELFARE_OFFICE.WRITE) ? (
                <WelfareOfficeGeneralForm />
              ) : (
                <PermissionError baseData />
              )
            }
          />
        </Layout>
      </Switch>
    </React.Fragment>
  );
};

export default Router;
